import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { tokenStorage } from '@services/tokenStorage'

// Use relative URLs - proxied through web server in both dev and production
const API_BASE_URL = ''

// Function to handle logout - will be set by the app on initialization
let logoutHandler: (() => void) | null = null

export function setLogoutHandler(handler: () => void) {
  logoutHandler = handler
}

function performLogout() {
  tokenStorage.clearTokens()
  if (logoutHandler) {
    logoutHandler()
  }
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let failedRequestsQueue: Array<(token: string) => void> = []

// Request interceptor - attach JWT token and handle auto-refresh
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token refresh for auth endpoints
    const isAuthEndpoint = config.url?.includes('/api/auth/')

    if (!isAuthEndpoint) {
      // Check if access token is expired or expiring soon
      if (tokenStorage.isAccessTokenExpired() && !tokenStorage.isRefreshTokenExpired()) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedRequestsQueue.push((token: string) => {
              if (config.headers) {
                config.headers.Authorization = `Bearer ${token}`
              }
              resolve(config)
            })
          })
        }

        isRefreshing = true

        try {
          const newToken = await refreshToken()
          if (newToken && config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`
          }

          // Process queued requests
          failedRequestsQueue.forEach(callback => callback(newToken))
          failedRequestsQueue = []
        } catch (error) {
          // Refresh failed, clear queue and logout
          failedRequestsQueue = []
          performLogout()
          throw error
        } finally {
          isRefreshing = false
        }
      } else {
        // Use current access token
        const token = tokenStorage.getAccessToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }
    } else {
      // For auth endpoints, just attach current token if available
      const token = tokenStorage.getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Log requests in development with full details
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`)
      console.log('[API Request] Full URL:', (config.baseURL || '') + (config.url || ''))
      console.log('[API Request] Headers:', config.headers)
      console.log('[API Request] Data:', config.data)
    }

    return config
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.url}`, response.status, response.data)
    }
    return response
  },
  async (error: AxiosError) => {
    // Log errors in development with full details
    if (import.meta.env.DEV) {
      console.error('[API Error] URL:', error.config?.url)
      console.error('[API Error] Status:', error.response?.status)
      console.error('[API Error] Message:', error.message)
      console.error('[API Error] Response:', error.response?.data)
      console.error('[API Error] Full Error:', error)
    }

    // Handle 401 Unauthorized - logout and redirect
    if (error.response?.status === 401) {
      // If refresh token is expired, logout
      if (tokenStorage.isRefreshTokenExpired()) {
        performLogout()

        // Only redirect if not already on a public auth page
        const currentPath = window.location.pathname
        const publicPaths = [
          '/login',
          '/register',
          '/register/verify-phone',
          '/register/complete',
          '/forgot-password',
          '/verify-reset-code',
          '/reset-password'
        ]
        if (!publicPaths.some(path => currentPath.startsWith(path))) {
          window.location.href = '/login'
        }
      }
    }

    // Handle USER_NOT_FOUND error - account no longer exists
    const errorData = error.response?.data as { code?: string } | undefined
    if (errorData?.code === 'USER_NOT_FOUND') {
      performLogout()

      // Redirect to login if not already on a public auth page
      const currentPath = window.location.pathname
      const publicPaths = [
        '/login',
        '/register',
        '/register/verify-phone',
        '/register/complete',
        '/forgot-password',
        '/verify-reset-code',
        '/reset-password'
      ]
      if (!publicPaths.some(path => currentPath.startsWith(path))) {
        window.location.href = '/login'
      }
    }

    // Handle other errors
    handleApiError(error)
    return Promise.reject(error)
  }
)

// Token refresh function
async function refreshToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken()

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await axios.post(
      '/api/auth/refresh',
      { refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      }
    )

    const { accessToken, refreshToken: newRefreshToken, expiresAt, refreshTokenExpiresAt } = response.data

    // Store new tokens
    tokenStorage.setTokens({
      accessToken,
      refreshToken: newRefreshToken,
      expiresAt,
      refreshTokenExpiresAt,
    })

    return accessToken
  } catch (error) {
    console.error('[Token Refresh Failed]', error)
    tokenStorage.clearTokens()
    throw error
  }
}

// Global error handler
function handleApiError(error: AxiosError) {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status
    const message = (error.response.data as any)?.message || error.message

    console.error(`[API Error ${status}]`, message, error.response.data)

    // Show user-friendly error messages
    switch (status) {
      case 400:
        console.error('Bad Request:', message)
        break
      case 401:
        console.error('Unauthorized:', message)
        break
      case 403:
        console.error('Forbidden:', message)
        break
      case 404:
        console.error('Not Found:', message)
        break
      case 500:
        console.error('Server Error:', message)
        break
      default:
        console.error('API Error:', message)
    }
  } else if (error.request) {
    // Request made but no response
    console.error('[Network Error] No response received', error.request)
  } else {
    // Request setup error
    console.error('[Request Error]', error.message)
  }
}

export default apiClient
